import { type AdminRsvpEntry, api } from "@/lib/api-client";
import { cn } from "@/lib/cn";
import { DEFAULT_CHILD_AGE_GROUPS, type Event, type RsvpConfig } from "@/lib/event-schema";
import { ArrowRight, Check, KeyRound, Loader2, Save, Trash2, Users } from "lucide-react";
import { useEffect, useState } from "react";

type Props = {
  config: RsvpConfig;
  event: Event;
};

type Mode = "new" | "edit";

/** Resize an age-range list to `count` entries, keeping existing picks. */
function resizeAges(prev: string[], count: number): string[] {
  return Array.from({ length: count }, (_, i) => prev[i] ?? "");
}

export function RsvpModule({ config, event }: Props) {
  const [mode, setMode] = useState<Mode>("new");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [familyName, setFamilyName] = useState("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [childrenAges, setChildrenAges] = useState<string[]>([]);
  const [passcode, setPasscode] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [foundRsvp, setFoundRsvp] = useState<AdminRsvpEntry | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [editAdults, setEditAdults] = useState(1);
  const [editChildren, setEditChildren] = useState(0);
  const [editChildrenAges, setEditChildrenAges] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const ageGroups = config.childAgeGroups?.length
    ? config.childAgeGroups
    : DEFAULT_CHILD_AGE_GROUPS;

  useEffect(() => {
    if (foundRsvp) {
      setEditAdults(foundRsvp.adults);
      setEditChildren(foundRsvp.children);
      setEditChildrenAges(resizeAges(foundRsvp.childrenAges ?? [], foundRsvp.children));
    }
  }, [foundRsvp]);

  function changeChildren(n: number) {
    setChildren(n);
    setChildrenAges((prev) => resizeAges(prev, n));
  }

  function changeChildAge(index: number, value: string) {
    setChildrenAges((prev) => prev.map((a, i) => (i === index ? value : a)));
  }

  function changeEditChildren(n: number) {
    setEditChildren(n);
    setEditChildrenAges((prev) => resizeAges(prev, n));
    setSavedFlash(false);
  }

  function changeEditChildAge(index: number, value: string) {
    setEditChildrenAges((prev) => prev.map((a, i) => (i === index ? value : a)));
    setSavedFlash(false);
  }

  function resetForm() {
    setStatus("idle");
    setFamilyName("");
    setAdults(2);
    setChildren(0);
    setChildrenAges([]);
    setPasscode("");
    setErrorMsg("");
    setFoundRsvp(null);
    setConfirmingCancel(false);
    setSavedFlash(false);
  }

  function switchMode(next: Mode) {
    setMode(next);
    setStatus("idle");
    setErrorMsg("");
    setFoundRsvp(null);
    setConfirmingCancel(false);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    // Validate before showing the spinner so a missing age doesn't flash the
    // button into a loading state on its way to an error.
    if (mode === "new" && children > 0 && childrenAges.some((a) => !a)) {
      setStatus("error");
      setErrorMsg("Please choose an age range for each child.");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      if (mode === "new") {
        await api.rsvp.add(event.slug, {
          familyName: familyName.trim(),
          adults,
          children,
          childrenAges,
          passcode: passcode || undefined,
        });
        setStatus("success");
      } else {
        const found = await api.rsvp.check(event.slug, familyName.trim(), passcode);
        setFoundRsvp(found);
        setStatus("success");
      }
    } catch (err) {
      setStatus("error");
      const message = err instanceof Error ? err.message : "Something went wrong.";
      if (mode === "edit" && /^404\b/.test(message)) {
        setErrorMsg("No RSVP found for that name and passcode.");
      } else {
        // Surface unexpected (non-HTTP) failures in the console; HTTP errors
        // already carry a "<status> ..." message the user sees.
        if (!(err instanceof Error) || !/^\d{3}\b/.test(err.message)) {
          console.error("rsvp submit failed", err);
        }
        setErrorMsg(message);
      }
    }
  }

  async function saveChanges() {
    if (!foundRsvp) return;
    // Validate before flipping into the saving state. We intentionally do not
    // setStatus("error") here: the edit view only renders while status is
    // "success", so the inline errorMsg is how the message surfaces.
    if (editChildren > 0 && editChildrenAges.some((a) => !a)) {
      setErrorMsg("Please choose an age range for each child.");
      return;
    }
    setSaving(true);
    setErrorMsg("");
    setSavedFlash(false);
    try {
      const updated = await api.rsvp.update(event.slug, foundRsvp.id, passcode, {
        adults: editAdults,
        children: editChildren,
        childrenAges: editChildrenAges,
      });
      setFoundRsvp(updated);
      setSavedFlash(true);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  }

  async function cancelRsvp() {
    if (!foundRsvp) return;
    setCancelling(true);
    setErrorMsg("");
    try {
      const result = await api.rsvp.remove(event.slug, foundRsvp.id, passcode);
      if (!result.success) {
        setErrorMsg("Could not cancel that RSVP. Check the passcode and try again.");
        return;
      }
      resetForm();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Failed to cancel RSVP.");
    } finally {
      setCancelling(false);
    }
  }

  return (
    <section
      id="rsvp"
      className="mx-auto w-full max-w-4xl px-5 py-16 sm:px-6 lg:px-8 lg:py-24"
      aria-labelledby={`${event.slug}-rsvp`}
    >
      <header className="text-center mb-10">
        <p className="eyebrow text-accent">Reserve a seat</p>
        <h2
          id={`${event.slug}-rsvp`}
          className="display-title mt-3 text-[clamp(2rem,4.4vw,3.25rem)] leading-tight"
        >
          {config.title}
        </h2>
        {config.description ? (
          <p className="mx-auto mt-3 max-w-2xl text-balance text-muted">{config.description}</p>
        ) : null}
      </header>

      <div className="surface-card overflow-hidden">
        <div className="grid gap-0 lg:grid-cols-[1fr,1.4fr]">
          {/* Mode picker */}
          <aside className="border-b border-line/60 bg-paper p-6 lg:border-b-0 lg:border-r">
            <p className="eyebrow mb-4">Choose</p>
            <div className="grid gap-2">
              <ModePill
                active={mode === "new"}
                onClick={() => switchMode("new")}
                icon={<Users className="h-4 w-4" strokeWidth={1.5} />}
                title="Register a family"
                body="First time RSVPing for this event."
              />
              <ModePill
                active={mode === "edit"}
                onClick={() => switchMode("edit")}
                icon={<KeyRound className="h-4 w-4" strokeWidth={1.5} />}
                title="Look up or cancel"
                body="Use your passcode to find or cancel your RSVP."
              />
            </div>
          </aside>

          {/* Form */}
          <form onSubmit={submit} className="p-6 lg:p-8">
            {status === "success" && mode === "new" ? (
              <div className="flex flex-col items-center text-center py-8">
                <div className="grid h-12 w-12 place-items-center rounded-full bg-accent/15 text-accent">
                  <Check className="h-5 w-5" strokeWidth={2} />
                </div>
                <h3 className="mt-4 font-display text-xl font-light">You're on the list</h3>
                <p className="mt-1 text-sm text-muted">
                  {passcode
                    ? "Keep your passcode somewhere safe — you'll need it to look up or cancel later."
                    : "Your RSVP has been recorded."}
                </p>
                <button
                  type="button"
                  onClick={resetForm}
                  className="mt-6 text-sm text-accent hover:underline"
                >
                  Register another family
                </button>
              </div>
            ) : status === "success" && mode === "edit" && foundRsvp ? (
              <FoundRsvpView
                rsvp={foundRsvp}
                adults={editAdults}
                children={editChildren}
                childrenAges={editChildrenAges}
                ageGroups={ageGroups}
                onAdultsChange={(n) => {
                  setEditAdults(n);
                  setSavedFlash(false);
                }}
                onChildrenChange={changeEditChildren}
                onChildAgeChange={changeEditChildAge}
                saving={saving}
                savedFlash={savedFlash}
                onSave={saveChanges}
                confirming={confirmingCancel}
                cancelling={cancelling}
                errorMsg={errorMsg}
                onStartCancel={() => setConfirmingCancel(true)}
                onAbortCancel={() => setConfirmingCancel(false)}
                onConfirmCancel={cancelRsvp}
                onClose={resetForm}
              />
            ) : (
              <div className="grid gap-5">
                <Field label="Family name">
                  <input
                    type="text"
                    required
                    placeholder="The Tahiri family"
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                    className="form-input"
                  />
                </Field>

                {mode === "new" ? (
                  <>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Adults">
                        <NumberStepper value={adults} onChange={setAdults} min={1} max={12} />
                      </Field>
                      <Field label="Children">
                        <NumberStepper
                          value={children}
                          onChange={changeChildren}
                          min={0}
                          max={12}
                        />
                      </Field>
                    </div>
                    <ChildAgeFields
                      count={children}
                      ages={childrenAges}
                      groups={ageGroups}
                      onChange={changeChildAge}
                      idPrefix="register"
                    />
                  </>
                ) : null}

                <Field
                  label={mode === "new" ? "Create a passcode (optional)" : "Your passcode"}
                  hint={
                    mode === "new"
                      ? "Optional. You'll need it only if you want to edit your RSVP later."
                      : "Six characters or so, set when you first RSVPed."
                  }
                >
                  <input
                    type="text"
                    required={mode !== "new"}
                    inputMode="text"
                    autoComplete="off"
                    placeholder="e.g. lantern-9"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    className="form-input"
                  />
                </Field>

                {status === "error" && errorMsg ? (
                  <p className="text-sm text-red-600 dark:text-red-400">{errorMsg}</p>
                ) : null}

                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-subtle">
                    {config.capacity ? `Capacity ~${config.capacity}` : "No fixed capacity"}
                  </p>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-canvas",
                      "dark:bg-accent dark:text-accent-on",
                      "shadow-glow transition-all hover:bg-ink/90 dark:hover:bg-accent/90 focus-ring",
                      "disabled:opacity-60",
                    )}
                  >
                    {status === "submitting" ? (
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                    ) : (
                      <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                    )}
                    {mode === "new" ? (config.ctaLabel ?? "Confirm RSVP") : "Find my RSVP"}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function ModePill({
  active,
  onClick,
  icon,
  title,
  body,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-3 rounded-2xl border p-3.5 text-left transition-all focus-ring",
        active
          ? "border-accent/60 bg-surface-raised shadow-card"
          : "border-line/60 bg-surface hover:border-line",
      )}
    >
      <span
        className={cn(
          "mt-0.5 grid h-8 w-8 place-items-center rounded-xl",
          active ? "bg-accent text-accent-on" : "bg-ink/5 text-ink dark:bg-white/10",
        )}
      >
        {icon}
      </span>
      <span>
        <span className="block text-sm font-medium text-ink">{title}</span>
        <span className="mt-0.5 block text-xs text-muted">{body}</span>
      </span>
    </button>
  );
}

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink">{label}</span>
      {children}
      {hint ? <span className="mt-1.5 block text-xs text-subtle">{hint}</span> : null}
    </label>
  );
}

function FoundRsvpView({
  rsvp,
  adults,
  children,
  childrenAges,
  ageGroups,
  onAdultsChange,
  onChildrenChange,
  onChildAgeChange,
  saving,
  savedFlash,
  onSave,
  confirming,
  cancelling,
  errorMsg,
  onStartCancel,
  onAbortCancel,
  onConfirmCancel,
  onClose,
}: {
  rsvp: AdminRsvpEntry;
  adults: number;
  children: number;
  childrenAges: string[];
  ageGroups: readonly string[];
  onAdultsChange: (n: number) => void;
  onChildrenChange: (n: number) => void;
  onChildAgeChange: (index: number, value: string) => void;
  saving: boolean;
  savedFlash: boolean;
  onSave: () => void;
  confirming: boolean;
  cancelling: boolean;
  errorMsg: string;
  onStartCancel: () => void;
  onAbortCancel: () => void;
  onConfirmCancel: () => void;
  onClose: () => void;
}) {
  const agesChanged =
    childrenAges.join("|") !== resizeAges(rsvp.childrenAges ?? [], children).join("|");
  const dirty = adults !== rsvp.adults || children !== rsvp.children || agesChanged;
  return (
    <div className="py-2">
      <div className="flex flex-col items-center text-center">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-accent/15 text-accent">
          <Check className="h-5 w-5" strokeWidth={2} />
        </div>
        <h3 className="mt-4 font-display text-xl font-light">{rsvp.familyName}</h3>
        <span className="mt-1 inline-block rounded-full bg-accent/15 px-2 py-0.5 text-[11px] uppercase tracking-[0.14em] text-accent">
          Confirmed
        </span>
        <p className="mt-3 max-w-sm text-xs text-subtle">
          Update head count below. To rename the family, cancel this RSVP and register again.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 rounded-2xl border border-line/60 bg-paper p-4 text-left">
        <div>
          <p className="text-xs uppercase tracking-wider text-subtle">Adults</p>
          <NumberStepper value={adults} onChange={onAdultsChange} min={1} max={12} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-subtle">Children</p>
          <NumberStepper value={children} onChange={onChildrenChange} min={0} max={12} />
        </div>
      </div>

      <ChildAgeFields
        count={children}
        ages={childrenAges}
        groups={ageGroups}
        onChange={onChildAgeChange}
        idPrefix="edit"
        className="mt-4"
      />

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-xs text-subtle">
          {savedFlash ? "Saved." : dirty ? "Unsaved changes." : "Up to date."}
        </p>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className={cn(
            "inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-medium text-canvas",
            "dark:bg-accent dark:text-accent-on",
            "shadow-glow transition-all hover:bg-ink/90 dark:hover:bg-accent/90 focus-ring",
            "disabled:opacity-50",
          )}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
          ) : (
            <Save className="h-4 w-4" strokeWidth={1.75} />
          )}
          Save changes
        </button>
      </div>

      {errorMsg ? <p className="mt-4 text-sm text-red-600 dark:text-red-400">{errorMsg}</p> : null}

      {confirming ? (
        <div className="mt-6 rounded-2xl border border-red-300/60 bg-red-50/60 p-4 dark:bg-red-950/30">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">Cancel this RSVP?</p>
          <p className="mt-1 text-xs text-red-700/80 dark:text-red-300/80">
            This removes the family from the guest list and can't be undone.
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={onAbortCancel}
              disabled={cancelling}
              className="flex-1 rounded-full border border-line/70 bg-surface px-4 py-2 text-sm text-ink hover:bg-surface-raised disabled:opacity-60"
            >
              Keep RSVP
            </button>
            <button
              type="button"
              onClick={onConfirmCancel}
              disabled={cancelling}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
            >
              {cancelling ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
              ) : (
                <Trash2 className="h-4 w-4" strokeWidth={1.75} />
              )}
              Yes, cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-muted hover:text-ink underline underline-offset-4"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onStartCancel}
            className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
            Cancel my RSVP
          </button>
        </div>
      )}
    </div>
  );
}

function ChildAgeFields({
  count,
  ages,
  groups,
  onChange,
  idPrefix,
  className,
}: {
  count: number;
  ages: string[];
  groups: readonly string[];
  onChange: (index: number, value: string) => void;
  /** Disambiguates select ids between the register and edit instances. */
  idPrefix: string;
  className?: string;
}) {
  if (count <= 0) return null;
  return (
    <div className={cn("rounded-2xl border border-line/60 bg-paper p-4", className)}>
      <p className="text-xs uppercase tracking-wider text-subtle">
        Age of each child
        <span className="ml-1 normal-case tracking-normal text-subtle/80">
          — helps us plan the right program
        </span>
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {Array.from({ length: count }, (_, i) => {
          const id = `${idPrefix}-child-age-${i}`;
          return (
            <div key={id} className="block">
              <label htmlFor={id} className="block text-xs font-medium text-ink">
                Child {i + 1}
              </label>
              <select
                id={id}
                required
                value={ages[i] ?? ""}
                onChange={(e) => onChange(i, e.target.value)}
                className="form-input mt-1.5"
              >
                <option value="" disabled>
                  Select age range
                </option>
                {groups.map((g) => (
                  <option key={g} value={g}>
                    {g} yrs
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NumberStepper({
  value,
  onChange,
  min,
  max,
}: {
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
}) {
  return (
    <div className="mt-1.5 grid grid-cols-[40px,1fr,40px] items-stretch overflow-hidden rounded-xl border border-line/70 bg-surface">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="text-ink/80 hover:bg-ink/5 dark:hover:bg-white/5 focus-ring"
        aria-label="Decrease"
      >
        −
      </button>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || 0)))}
        min={min}
        max={max}
        className="bg-transparent text-center text-base font-medium focus-ring outline-none"
      />
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        className="text-ink/80 hover:bg-ink/5 dark:hover:bg-white/5 focus-ring"
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
}
